"use client";

import { c as _c } from "react/compiler-runtime";
import { Check, History, Loader2, RefreshCw, ScanSearch } from "lucide-react";
import type { ReactNode } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
export interface AssistantConversationSummary {
  id: string;
  title: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}
function formatSessionDate(value: Date | string | null) {
  if (!value) return "Date unavailable";
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}
export function AssistantSessionToolbar(t0) {
  const $ = _c(29);
  const {
    conversations,
    activeConversationId,
    isDemo,
    isStreaming,
    isHistoryLoading,
    isHistoryOpen,
    canScanSavedChapter,
    loadingConversationId,
    historyError,
    onStartNew,
    onRefreshHistory,
    onHistoryOpenChange,
    onOpenConversation,
    onScanSavedChapter,
    voiceTools
  } = t0;
  let t1;
  if ($[0] !== activeConversationId || $[1] !== conversations || $[2] !== historyError || $[3] !== isDemo || $[4] !== isHistoryLoading || $[5] !== isHistoryOpen || $[6] !== isStreaming || $[7] !== loadingConversationId || $[8] !== onHistoryOpenChange || $[9] !== onOpenConversation || $[10] !== onRefreshHistory) {
    t1 = !isDemo && <DropdownMenu open={isHistoryOpen} onOpenChange={open => {
      onHistoryOpenChange(open);
      if (open) {
        onRefreshHistory();
      }
    }}><HoverCard openDelay={100} closeDelay={200}><HoverCardTrigger asChild={true}><DropdownMenuTrigger asChild={true}><button type="button" className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50" aria-label="View past assistant sessions" disabled={isStreaming}><History className="size-4" /></button></DropdownMenuTrigger></HoverCardTrigger><HoverCardContent role="tooltip" className="w-auto max-w-72 px-3 py-2 text-sm" side="top">Past sessions</HoverCardContent></HoverCard><DropdownMenuContent align="end" className="w-72 border-primary/40 bg-secondary text-foreground shadow-xl shadow-black/30 ring-1 ring-primary/20"><DropdownMenuLabel>Past sessions</DropdownMenuLabel>{isHistoryLoading && conversations.length === 0 ? <DropdownMenuItem disabled={true}><Loader2 className="animate-spin" /> Loading…</DropdownMenuItem> : historyError ? <DropdownMenuItem disabled={true} className="text-destructive">{historyError}</DropdownMenuItem> : conversations.length === 0 ? <DropdownMenuItem disabled={true}>No past sessions yet</DropdownMenuItem> : conversations.map(conversation => <DropdownMenuItem key={conversation.id} onSelect={event => {
          event.preventDefault();
          onOpenConversation(conversation.id);
        }} disabled={loadingConversationId === conversation.id} className="items-start">{loadingConversationId === conversation.id ? <Loader2 className="mt-0.5 animate-spin" /> : <Check className={`mt-0.5 ${activeConversationId === conversation.id ? "opacity-100" : "opacity-0"}`} />}<span className="min-w-0"><span className="block truncate">{conversation.title || "Untitled session"}</span><span className="block text-xs text-muted-foreground">{formatSessionDate(conversation.updatedAt ?? conversation.createdAt)}</span></span></DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu>;
    $[0] = activeConversationId;
    $[1] = conversations;
    $[2] = historyError;
    $[3] = isDemo;
    $[4] = isHistoryLoading;
    $[5] = isHistoryOpen;
    $[6] = isStreaming;
    $[7] = loadingConversationId;
    $[8] = onHistoryOpenChange;
    $[9] = onOpenConversation;
    $[10] = onRefreshHistory;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== canScanSavedChapter || $[13] !== isStreaming || $[14] !== loadingConversationId || $[15] !== onScanSavedChapter) {
    t2 = canScanSavedChapter && <HoverCard openDelay={100} closeDelay={200}><HoverCardTrigger asChild={true}><button type="button" onClick={onScanSavedChapter} className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50" aria-label="Scan saved chapter for Wiki and plot changes" disabled={isStreaming || loadingConversationId !== null}><ScanSearch className="size-4" /></button></HoverCardTrigger><HoverCardContent role="tooltip" className="w-auto max-w-72 px-3 py-2 text-sm" side="top">Scan saved chapter</HoverCardContent></HoverCard>;
    $[12] = canScanSavedChapter;
    $[13] = isStreaming;
    $[14] = loadingConversationId;
    $[15] = onScanSavedChapter;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <RefreshCw className="size-4" />;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== isStreaming || $[19] !== onStartNew) {
    t4 = <HoverCardTrigger asChild={true}><button type="button" onClick={onStartNew} className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50" aria-label="Start a new assistant session" disabled={isStreaming}>{t3}</button></HoverCardTrigger>;
    $[18] = isStreaming;
    $[19] = onStartNew;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <HoverCardContent role="tooltip" className="w-auto max-w-72 px-3 py-2 text-sm" side="top">New session</HoverCardContent>;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t4) {
    t6 = <HoverCard openDelay={100} closeDelay={200}>{t4}{t5}</HoverCard>;
    $[22] = t4;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t1 || $[25] !== t2 || $[26] !== t6 || $[27] !== voiceTools) {
    t7 = <><div className="flex h-10 shrink-0 items-center justify-end gap-1 border-b border-border px-2">{voiceTools}{t1}{t2}{t6}</div></>;
    $[24] = t1;
    $[25] = t2;
    $[26] = t6;
    $[27] = voiceTools;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
}